// Phase 8.4 — Path-based tenant routing.
//
// The backend already enforces tenant isolation via JWT (`tenant_slug` claim)
// and Postgres RLS, so the URL slug is *cosmetic* — it doesn't widen access.
// What it *does* give us:
//   * Bookmarkable per-tenant URLs (e.g. /t/cedar/patients/123)
//   * A "tenant in scope" hint we can show in the UI
//   * A clean redirect target for platform-admin impersonation
//   * A stepping stone toward subdomain routing in real production
//
// Backwards-compat: legacy URLs like /patients still work — they're treated as
// "current user's home tenant" (resolved server-side from the JWT).

import { createContext, useContext } from 'react';
import { useParams, Outlet, Navigate } from 'react-router-dom';
import { useAuth } from './auth.jsx';
import Layout from '../components/Layout.jsx';
import Landing from '../pages/Landing.jsx';

const TenantPathCtx = createContext({ slug: null });

/**
 * Hook: returns the slug currently in scope (from /t/:slug/*) or null on
 * legacy URLs.  Components don't need to know whether the slug came from
 * the URL or from the JWT — they just call useTenantHref() to build links.
 */
export const useTenantSlug = () => useContext(TenantPathCtx).slug;

/**
 * Hook: returns a function that prefixes paths with /t/<slug> when a slug
 * is in scope.  Use this whenever you'd otherwise hardcode "/patients" etc:
 *
 *   const href = useTenantHref();
 *   <NavLink to={href('/patients')}>Patients</NavLink>
 */
export function useTenantHref() {
  const slug = useTenantSlug();
  return (path) => buildTenantHref(slug, path);
}

/**
 * Pure helper for places that don't have a hook (e.g., post-login redirect).
 * Idempotent: passing an already-prefixed path returns it unchanged.
 */
export function buildTenantHref(slug, path) {
  if (!slug) return path || '/';
  if (!path || path === '/') return `/t/${slug}/`;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith(`/t/${slug}`)) return path;
  if (path.startsWith('/t/'))         return path; // different tenant — leave alone
  return `/t/${slug}${path.startsWith('/') ? '' : '/'}${path}`;
}

/**
 * Route element for /t/:slug/*  — mirrors the unprefixed Root() in App.jsx
 * but injects the slug into context so child links auto-prefix.
 *
 * Auth contract:
 *   - Signed-out users land on /login (so the auth flow can deposit them
 *     back at /t/<slug>/ on success).
 *   - Signed-in users hit the normal Layout shell, just under a different URL.
 */
export function TenantRoot() {
  const { slug } = useParams();
  const { user } = useAuth();

  // Optional: warn-but-allow if URL slug != user's actual tenant.
  // The backend will still scope all data to the JWT's tenant, so this
  // isn't a security issue — just a nav UX nudge for impersonation flows.
  // We let it through silently for now; future: surface a banner.

  if (!user) {
    // Stash intended slug for /login → return.
    return <Navigate to={`/login?tenant=${encodeURIComponent(slug)}`} replace />;
  }
  if (user.must_change_password && !window.location.pathname.endsWith('/change-password')) {
    return <Navigate to="/change-password" replace />;
  }

  return (
    <TenantPathCtx.Provider value={{ slug }}>
      <Layout />
    </TenantPathCtx.Provider>
  );
}

/**
 * Wrapper for the legacy unprefixed app shell so useTenantSlug() still
 * returns null cleanly (instead of throwing) outside /t/:slug/*.
 */
export function NoTenantPath({ children }) {
  return <TenantPathCtx.Provider value={{ slug: null }}>{children}</TenantPathCtx.Provider>;
}
